import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Hook to extract and manage Copilot context from current page state
 */
export const useCopilotContext = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [context, setContext] = useState({
    module: '',
    path: '',
    entity: {},
    filters: {},
  });

  // Extract module from path
  const getModuleFromPath = useCallback((pathname) => {
    const pathMap = {
      '/dashboard': 'dashboard',
      '/askstewie': 'askstewie',
      '/canvass': 'quickcanvass',
      '/command-center': 'commandcenter',
      '/station-log': 'stationlog',
      '/rfid': 'rfid',
      '/admin': 'admin',
    };
    
    for (const [path, module] of Object.entries(pathMap)) {
      if (pathname.startsWith(path)) {
        return module;
      }
    }
    return 'platform';
  }, []);

  // Update context when location changes
  useEffect(() => {
    const module = getModuleFromPath(location.pathname);
    
    // Parse URL params for filters
    const params = new URLSearchParams(location.search);
    const filters = {};
    if (params.get('date_from')) filters.date_from = params.get('date_from');
    if (params.get('date_to')) filters.date_to = params.get('date_to');
    if (params.get('status')) filters.status = params.get('status');
    
    setContext(prev => ({
      ...prev,
      module,
      path: location.pathname,
      filters,
    }));
  }, [location, getModuleFromPath]);

  // Update entity context (called by pages when selecting items)
  const setEntityContext = useCallback((entity) => {
    setContext(prev => ({
      ...prev,
      entity: { ...prev.entity, ...entity },
    }));
  }, []);

  // Clear entity context
  const clearEntityContext = useCallback(() => {
    setContext(prev => ({
      ...prev,
      entity: {},
    }));
  }, []);

  // Update filters
  const setFilters = useCallback((filters) => {
    setContext(prev => ({
      ...prev,
      filters: { ...prev.filters, ...filters },
    }));
  }, []);

  // Get full context with user info
  const getFullContext = useCallback(() => {
    return {
      ...context,
      user_id: user?.id,
      user_roles: user?.roles || [],
    };
  }, [context, user]);

  return {
    context,
    setEntityContext,
    clearEntityContext,
    setFilters,
    getFullContext,
  };
};

export default useCopilotContext;
